import { useEffect, useMemo } from 'react';
import noop from 'lodash/noop';
import {
	useEpicsStoreIsInitialised,
	useHasFullFetchStarted,
} from '@atlassian/jira-backlog-epics-store/src/selectors/index.tsx';
import { useEpicsPrefetch } from '@atlassian/jira-software-backlog-panel/src/controllers/use-epics-refetch/index.tsx';
import { getViewSettingsPanels as getViewSettingsPanelsFromLocalStorage } from '@atlassian/jira-software-view-settings/src/services/local-storage/index.tsx';
import { View } from '@atlassian/jira-software-view-settings/src/common/types/constant.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useEpicQueryLite } from '../../../services/queries/epics-lite/index.tsx';
import {
	loadIssueParentsFailure,
	loadIssueParentsSuccess,
} from '../../../state/actions/issue-parents/load/index.tsx';
import { useBacklogDispatch, useBacklogSelector } from '../../../state/index.tsx';
import { getFirstLevelHierarchyName } from '../../../state/selectors/issue-hierarchy/index.tsx';
import { useIsCMPBacklog } from '../../../state/state-hooks/capabilities.tsx';
import { getIssueParents, transformLiteQueryData } from './utils.tsx';

export const useInitEpicData = (boardId: number) => {
	const dispatch = useBacklogDispatch();
	const isCompanyManaged = useIsCMPBacklog();
	const firstLevelHierarchyName = useBacklogSelector(getFirstLevelHierarchyName);

	const [isInitialised, { initState, resetState, updateQueryState, updateEpicsAttributes }] =
		useEpicsStoreIsInitialised();

	const [hasFullFetchStarted] = useHasFullFetchStarted();

	let prefetch: ReturnType<typeof useEpicsPrefetch>['prefetch'] = noop;
	if (fg('prefetch-epics-data-backlog')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		prefetch = useEpicsPrefetch({ boardId, isCompanyManaged }).prefetch;
	}
	const { data, loading, error } = useEpicQueryLite(boardId, isCompanyManaged);

	const initData = useMemo(
		() => transformLiteQueryData(data, isCompanyManaged, firstLevelHierarchyName),
		[data, firstLevelHierarchyName, isCompanyManaged],
	);

	useEffect(() => {
		if (error) {
			updateEpicsAttributes({ isLoading: false, hasError: true });
		}
		updateQueryState({ loading, error });
	}, [loading, error, updateQueryState, updateEpicsAttributes]);

	// Populate redux store with issue parents data
	useEffect(() => {
		if (isInitialised || loading) {
			return;
		}

		if (error) {
			dispatch(loadIssueParentsFailure());
			return;
		}

		const issueParents = getIssueParents(initData.cards);
		issueParents && dispatch(loadIssueParentsSuccess(issueParents));
	}, [initData, isInitialised, dispatch, loading, error]);

	// Initialise epic store
	useEffect(() => {
		if (data && !loading && !isInitialised) {
			initState({
				...initData,
			});
		}
	}, [boardId, data, initData, initState, isCompanyManaged, isInitialised, loading]);

	// Prefetch full epic details if the epic panel is open in the initial render
	if (fg('prefetch-epics-data-backlog')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		useEffect(() => {
			const { EPIC_PANEL } = getViewSettingsPanelsFromLocalStorage({
				boardId,
				view: View.BACKLOG,
			});

			if (EPIC_PANEL && !hasFullFetchStarted) {
				prefetch();
			}
		}, [hasFullFetchStarted, prefetch, boardId]);
	}

	useEffect(
		() => () => {
			resetState();
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);
};
